"use client";

import { Globe2, ScrollText, Users } from "lucide-react";
import { memo } from "react";
import { UseFormReturn, useFormState, useWatch } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { IVisaSearchSchema } from "../../../search/types/visa.types";
import { TravelerSelect } from "./traveler-select";
import { VisaCountrySelect } from "./visa-county-select";
import { VisaTypeSelect } from "./visa-type-select";

interface SummaryFieldProps {
  totalTravelers: number;
}

interface VisaFieldProps {
  form: UseFormReturn<IVisaSearchSchema>;
}

const SUMMARY_BUTTON_CLASS =
  "pointer-events-none h-8 rounded-lg border border-border/60 bg-background/85 px-2 text-xs font-semibold text-foreground shadow-xs md:rounded-md md:border-0 md:bg-primary/10 md:px-4 md:text-sm md:text-primary md:shadow-none";

const SearchType = memo(function SearchType() {
  return (
    <Button
      type="button"
      variant="ghost"
      className={SUMMARY_BUTTON_CLASS}
    >
      <Globe2 className="h-4 w-4" />
      Visa
    </Button>
  );
});

const Travelers = memo(function Travelers({
  totalTravelers,
}: Pick<SummaryFieldProps, "totalTravelers">) {
  return (
    <Button
      type="button"
      variant="ghost"
      className={SUMMARY_BUTTON_CLASS}
    >
      <Users className="h-4 w-4" />
      {totalTravelers} Traveler{totalTravelers > 1 ? "s" : ""}
    </Button>
  );
});

const VisaType = memo(function VisaType({ form }: VisaFieldProps) {
  const visaType = useWatch({ control: form.control, name: "visa_type" });

  return (
    <Button
      type="button"
      variant="ghost"
      className={SUMMARY_BUTTON_CLASS}
    >
      <ScrollText className="h-4 w-4" />
      {visaType?.name || "All visa types"}
    </Button>
  );
});

const Country = memo(function Country({ form }: VisaFieldProps) {
  const country = useWatch({ control: form.control, name: "country" });
  const { errors } = useFormState({ control: form.control, name: "country" });
  const error = errors.country;

  return (
    <VisaCountrySelect
      value={country}
      onChange={(nextCountry) => {
        form.setValue("country", nextCountry, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }}
      label="Destination Country"
      placeholder="Select destination country"
      invalid={Boolean(error)}
      popoverModal
    />
  );
});

const Type = memo(function Type({ form }: VisaFieldProps) {
  const visaType = useWatch({ control: form.control, name: "visa_type" });
  const { errors } = useFormState({
    control: form.control,
    name: "visa_type",
  });
  const error = errors.visa_type;

  return (
    <VisaTypeSelect
      value={visaType}
      onChange={(nextType) => {
        form.setValue("visa_type", nextType, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }}
      label="Visa Type"
      placeholder="All visa types"
      invalid={Boolean(error)}
      popoverModal
    />
  );
});

const Traveler = memo(function Traveler({ form }: VisaFieldProps) {
  const travelerCount = useWatch({ control: form.control, name: "travelers" });
  const { errors } = useFormState({
    control: form.control,
    name: "travelers",
  });
  const error = errors.travelers;

  return (
    <TravelerSelect
      value={travelerCount}
      onChange={(nextCount) => {
        form.setValue("travelers", nextCount, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }}
      label="Travelers"
      placeholder="Select travelers"
      invalid={Boolean(error)}
      popoverModal
      min={1}
      max={20}
    />
  );
});

export const VisaFields = {
  SearchType,
  VisaType,
  Travelers,
  Country,
  Type,
  Traveler,
};
